import { Alert, Col, Table, Tag } from "antd";
import { Row } from "antd/lib";
import React, { useEffect, useState } from "react";
import { CardContainer } from "../../components/Card/CardContainer";
import Text from "../../components/Text/Text";
import { ExclamationCircleOutlined, UnorderedListOutlined } from "@ant-design/icons";
import { color } from "../../resource";
import SearchInput from "../../components/Input/SearchInput";
import Select from "../../components/Select/Select";
import { FlexCol } from "../../components/Container/Container";
import {
  InvoiceOrderStatus,
  invoiceOrderStatusColor,
  invoiceOrderStatusLabel,
} from "../../definitions/invoice";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { getInvoiceByComp } from "../../datasource/InvoiceDatasource";
import { dateFormatter, numberFormatter } from "../../utility/Formatter";
import dayjs from "dayjs";
type FixedType = "left" | "right" | boolean;

function IndexInvoiceList() {
  const navigate = useNavigate();
  const userProfile = JSON.parse(localStorage.getItem("profile")!);
  const [searchParams, setSearchParams] = useSearchParams();
  const pageSize = 10;
  const [page, setPage] = useState<string>(searchParams.get("page") || "1");
  const [data, setData] = useState<any>();
  const [search, setSearch] = useState<string>(searchParams.get("search") || "");
  const [searchStatus, setSearchStatus] = useState<any>(
    searchParams
      .get("paymentStatuses")
      ?.split(",")
      ?.filter((x) => Object.keys(invoiceOrderStatusLabel).includes(x) || undefined),
  );

  const getData = async () => {
    setSearchParams({
      company: userProfile.company,
      page: `${page}`,
      pageSize: `${pageSize}`,
      search: search,
      paymentStatuses: (searchStatus || "").toString(),
    });
    await getInvoiceByComp({
      company: userProfile.company,
      page: page,
      pageSize: pageSize,
      search,
      paymentStatuses: searchStatus,
    }).then((res) => {
      setData(res);
    });
  };

  useEffect(() => {
    getData();
  }, [page, search, searchStatus]);

  const PageTitle = () => {
    return (
      <>
        <Row justify={"space-between"}>
          <Col>
            <Text level={3} fontWeight={700}>
              ใบแจ้งหนี้ / เอกสาร
            </Text>
            <br />
            <Text level={6}>อัปเดทล่าสุดเมื่อ {dateFormatter(data?.updateDate, true)}</Text>
          </Col>
        </Row>
        <br />
        <Alert
          message={
            <Text level={6} color='Grey'>
              เพื่อให้ข้อมูลรายการถูกต้องสมบูรณ์ กรุณาตรวจสอบหรืออัปเดตในระบบภายใน 7 วัน เช่น
              จำนวนเงิน, การชำระเงิน, สถานะต่างๆ เป็นต้น
            </Text>
          }
          style={{
            backgroundColor: color.background1,
            borderColor: color.background1,
            borderRadius: "2px",
          }}
          icon={<ExclamationCircleOutlined />}
          showIcon
        />
        <br />
        <Row justify={"space-between"}>
          <Col span={10}>
            <SearchInput
              value={search}
              placeholder='ค้นหาชื่อร้านค้า / รหัสเอกสาร (Invoice, CN, JV)'
              onChange={(e) => setSearch(e.target.value.toUpperCase())}
            />
          </Col>
          <Col span={4}>
            <Select
              defaultValue={searchStatus}
              data={Object.entries(invoiceOrderStatusLabel)
                .filter(
                  ([key]) => key !== InvoiceOrderStatus.ALL && key !== InvoiceOrderStatus.CANCELED,
                )
                .map(([key, label]) => ({
                  key,
                  label,
                  value: key,
                }))}
              onChange={(val) => {
                setSearchStatus(val);
              }}
              placeholder='สถานะทั้งหมด'
              mode='multiple'
              maxTagCount={1}
            ></Select>
          </Col>
        </Row>
      </>
    );
  };

  const columns = [
    {
      title: (
        <Text level={6} fontWeight={600}>
          รหัสเอกสาร
        </Text>
      ),
      dataIndex: "invoiceNo",
      key: "invoiceNo",
      render: (value: any, row: any, index: number) => {
        return (
          <FlexCol>
            <Text level={5}>{value}</Text>
            <Text level={6} color='Text3'>
              {dateFormatter(row.postingDate)}
            </Text>
          </FlexCol>
        );
      },
    },
    {
      title: (
        <Text level={6} fontWeight={600}>
          ชื่อร้านค้า
        </Text>
      ),
      dataIndex: "billCusName",
      key: "billCusName",
      render: (value: any, row: any, index: number) => {
        return (
          <FlexCol>
            <Text>{value}</Text>
          </FlexCol>
        );
      },
    },
    {
      title: (
        <Text level={6} fontWeight={600}>
          กำหนดชำระ
        </Text>
      ),
      dataIndex: "dueDate",
      key: "dueDate",
      render: (value: any, row: any, index: number) => {
        const overdue = dayjs(value).diff(dayjs(), "days") * -1;
        return (
          <FlexCol>
            <Text level={5}>{dateFormatter(value)}</Text>
            {row.paymentStatus === "ALREADY_PAID" ? (
              <Text level={6} color={"Text3"}>
                -
              </Text>
            ) : (
              <Text level={6} color={overdue >= 0 ? "error" : "Text3"}>
                {overdue >= 0 ? "เกิน " + overdue + " วัน" : "-"}
              </Text>
            )}
          </FlexCol>
        );
      },
    },
    {
      title: (
        <Text level={6} fontWeight={600}>
          ยอดรวม
        </Text>
      ),
      dataIndex: "amt",
      key: "amt",
      render: (value: any, row: any, index: number) => {
        return (
          <FlexCol>
            <Text level={5}>{numberFormatter(value, 2)}</Text>
            <Text level={6} color='Text3'>
              บาท
            </Text>
          </FlexCol>
        );
      },
    },
    {
      title: (
        <Text level={6} fontWeight={600}>
          PDC
        </Text>
      ),
      dataIndex: "amtPDC",
      key: "amtPDC",
      render: (value: any, row: any, index: number) => {
        const sumPdc =
          (row.amtPDC || 0) +
          (row.invoicePayments?.reduce(
            (acc, inv) => (inv?.paymentChannel?.includes("เช็ค") ? acc + inv?.rvAmt : acc),
            0,
          ) || 0);
        return (
          <FlexCol>
            <Text level={5}>{numberFormatter(sumPdc, 2)}</Text>
            <Text level={6} color='Text3'>
              บาท
            </Text>
          </FlexCol>
        );
      },
    },
    {
      title: (
        <Text level={6} fontWeight={600}>
          เงินโอน
        </Text>
      ),
      dataIndex: "amtLCY",
      key: "amtLCY",
      render: (value: any, row: any, index: number) => {
        const sumPym = row?.invoicePayments
          .filter((x) => x?.paymentChannel?.includes("เงินโอน"))
          ?.reduce((acc, inv) => acc + inv?.rvAmt, 0);
        return (
          <FlexCol>
            <Text level={5}>{numberFormatter(sumPym, 2)}</Text>
            <Text level={6} color='Text3'>
              บาท
            </Text>
          </FlexCol>
        );
      },
    },
    {
      title: (
        <Text level={6} fontWeight={600}>
          ชำระอื่นๆ
        </Text>
      ),
      dataIndex: "orderNo",
      key: "orderNo",
      render: (value: any, row: any, index: number) => {
        const sumPym = row?.invoicePayments?.reduce(
          (acc, inv) =>
            !inv.paymentChannel?.includes("เช็ค") && !inv.paymentChannel?.includes("เงินโอน")
              ? acc + inv?.rvAmt
              : acc,
          0,
        );
        return (
          <FlexCol>
            <Text level={5}>{numberFormatter(sumPym, 2)}</Text>
            <Text level={6} color='Text3'>
              บาท
            </Text>
          </FlexCol>
        );
      },
    },
    {
      title: (
        <Text level={6} fontWeight={600}>
          ยอดคงเหลือ
        </Text>
      ),
      dataIndex: "total",
      key: "total",
      fixed: "right" as FixedType | undefined,
      render: (value: any, row: any, index: number) => {
        const sum = row?.invoicePayments?.reduce((acc, inv) => acc + inv?.rvAmt, 0);
        return (
          <FlexCol>
            <Text level={5}>{numberFormatter(row.amt - sum, 2)}</Text>
            <Text level={6} color='Text3'>
              บาท
            </Text>
          </FlexCol>
        );
      },
    },
    {
      title: (
        <Text level={6} fontWeight={600}>
          สถานะ
        </Text>
      ),
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      fixed: "right" as FixedType | undefined,
      render: (value: any, row: any, index: number) => {
        const map = {
          ALREADY_PAID: InvoiceOrderStatus.COMPLETE,
          PARTIAL_PAYMENT: InvoiceOrderStatus.NON_COMPLETE,
          WAITING_PAID: InvoiceOrderStatus.NON_PAYMENT,
        };
        const sumPym = row.invoicePayments?.reduce((acc, inv) => acc + inv?.rvAmt, 0);
        let mapStatus;
        if (value) {
          mapStatus = map[value];
        } else if (sumPym === 0) {
          mapStatus = InvoiceOrderStatus.NOT_USED;
        } else if (parseFloat(row.amt) > sumPym) {
          mapStatus = InvoiceOrderStatus.SOME_USED;
        } else {
          mapStatus = InvoiceOrderStatus.USED;
        }
        return (
          <FlexCol>
            <Tag color={invoiceOrderStatusColor[mapStatus]} style={{ borderRadius: 12 }}>
              <Text color='white' fontWeight={600} fontSize={14}>
                {invoiceOrderStatusLabel[mapStatus]}
              </Text>
            </Tag>
          </FlexCol>
        );
      },
    },
    {
      title: "",
      dataIndex: "invoiceId",
      key: "invoiceId",
      fixed: "right" as FixedType | undefined,
      render: (value: any, row: any, index: number) => {
        return (
          <FlexCol>
            <div
              className='btn btn-icon btn-light btn-hover-primary btn-sm'
              onClick={() => navigate(`/order/invoice-detail/${value}`)}
            >
              <span className='svg-icon svg-icon-primary svg-icon-2x'>
                <UnorderedListOutlined style={{ color: color["primary"] }} />
              </span>
            </div>
          </FlexCol>
        );
      },
    },
  ];

  return (
    <CardContainer>
      {PageTitle()}
      <br />
      <Table
        dataSource={data?.data || []}
        columns={columns}
        scroll={{ x: "max-content" }}
        pagination={{
          pageSize,
          current: Number(page),
          total: data?.count,
          showSizeChanger: false,
          position: ["bottomCenter"],
          onChange: (page) => {
            setPage(String(page));
          },
        }}
        size='large'
        tableLayout='fixed'
      />
    </CardContainer>
  );
}
export default IndexInvoiceList;
